<template>
  <div class="emat-number">
    <div class="emat-number-input">
      <span v-if="!editable" class="value" v-text="currentValue"></span>
      <input class="value" type="number" v-else v-model.number="currentValue">
      <div class="btn-group-arrows">
        <div class="emat-btn emat-btn-primary plus" :class="{'disabled': incrementIsDisabled}" @click="increment"></div>
        <div class="emat-btn emat-btn-primary minus" :class="{'disabled': decrementIsDisabled}" @click="decrement"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NumberInput',

  data() {
    return {
      currentValue: 0,
    };
  },

  props: {
    /**
     * Unique name for your input
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * The current selected value {use as: [:value="value"]}
     */
    value: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     *
     */
    min: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     *
     */
    max: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     *
     */
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    onChange(val) {
      /**
       * Output the input for v-model
       */
      this.$emit('input', val);

      /**
       * Output the change with the new value
       */
      this.$emit('change', val);
    },
    increment() {
      this.currentValue++;
    },
    decrement() {
      this.currentValue--;
    },
  },

  computed: {
    decrementIsDisabled() {
      return this.currentValue <= this.min;
    },
    incrementIsDisabled() {
      if (!this.max) return false;
      return this.currentValue >= this.min;
    }
  },

  watch: {
    value: {
      deep: true,
      handler(val) {
        this.currentValue = val;
      },
    },
    currentValue: {
      deep: true,
      handler(val) {
        if (val === '') return;
        if (val < this.min) this.currentValue = this.min;
        if (this.max !== null && val > this.max) this.currentValue = this.max;

        this.onChange(this.currentValue);
      },
    },
  },

  created() {
    this.currentValue = this.value !== null ? this.value : 0;
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/sass/abstracts";
.emat-number {
  height: 48px;
  width: 52px;
}

.emat-number-input {
  display: flex;
  min-width: 32px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid var(--c-number-border);
  background: #fff;
  font-size: 13px;
  font-family: var(--font);
  border-bottom-left-radius: $border-radius;
  border-top-left-radius: $border-radius;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.05);
  align-items: center;
  overflow: hidden;
  margin-right: 10px;

  .value {
    display: block;
    flex-grow: 1;
    flex-shrink: 1;
    text-align: center;
    width: 10px;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    border: none;
    outline: none;
    font-family: var(--font);
    font-size: 12px;
    align-items: center;

    &[type=number] {
      -moz-appearance: textfield;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .btn-group-arrows {
    height: 100%;
    display: flex;
    flex-direction: column;
    width: 16px;

    .emat-btn {
      width: 16px;
      height: 16px;
      font-size: 10px;
      line-height: 8px;
      text-align: center;
      position: relative;
      border: none;
      cursor: pointer;

      &.disabled {
        cursor: not-allowed;
      }

      &:focus, :active {
        outline: none;
      }
    }

    .emat-btn.plus {
      background-color: var(--c-number-plus-background);
      transition: scale .3s cubic-bezier(.165, .84, .44, 1);

      &.disabled {
        opacity: 0.5;
      }

      &:hover {
        background-color: var(--c-number-plus-background-hover);
      }

      &:before {
        width: 2px;
        height: 8px;
        content: "";
        position: absolute;
        background: var(--c-number-plus-minus-symbol);
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
      }

      &:after {
        width: 8px;
        height: 2px;
        content: "";
        position: absolute;
        background: var(--c-number-plus-minus-symbol);
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
      }
    }

    .emat-btn.minus {
      background-color: var(--c-number-minus-background);
      opacity: 0.7;
      transition: scale .3s cubic-bezier(.165, .84, .44, 1);

      &.disabled {
        opacity: 0.4;
      }

      &:after {
        width: 8px;
        height: 2px;
        content: "";
        position: absolute;
        background: var(--c-number-plus-minus-symbol);
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
      }
    }

    > .emat-btn.emat-btn-primary.plus, .emat-btn.emat-btn-primary.minus {
      height: 50%;
    }
  }
}
</style>
